import { AppState, HabitType } from '@/types';
import { calculateHabiticaReward, shouldResetDailyCompletions, getHabiticaColorState, HABITICA_VALUE_CONFIG, calculateDailyMissPenalty, calculateNegativeHabitPenalty, getConstitutionModifier } from '@/lib/habitValueSystem';
import { saveHabitToSupabase, saveDailyToSupabase, saveTodoToSupabase, markDailyCompleteInSupabase, markDailyUncompleteInSupabase, markTodoCompleteInSupabase, markTodoUncompleteInSupabase, logHabitCompletionToSupabase, deleteHabitFromSupabase, deleteDailyFromSupabase, deleteTodoFromSupabase } from '@/utils/supabaseHabitSaver';

export interface HabiticaActions {
  createHabit: (habit: Omit<HabitType, 'id' | 'streak' | 'completedToday' | 'lastCompleted'>) => Promise<void>;
  updateHabit: (habitId: string, habit: Partial<HabitType>) => Promise<void>;
  completeHabit: (habitId: string) => Promise<void>;
  uncompleteHabit: (habitId: string) => Promise<void>;
  deleteHabit: (habitId: string) => Promise<void>;
  clickNegativeHabit: (habitId: string) => void;
  takeDamage: (amount: number, reason: string) => void;
  applyDailyDamage: () => void;
}

export const createHabiticaActions = (
  state: AppState,
  dispatch: React.Dispatch<any>
): HabiticaActions => {
  return {
    createHabit: async (habitData) => {
      console.log('🔥 createHabit action called with data:', habitData);
      
      const habit: HabitType = {
        ...habitData,
        id: crypto.randomUUID(),
        userId: state.user?.id || '',
        streak: 0,
        completedToday: false,
        lastCompleted: undefined,
        habitValue: HABITICA_VALUE_CONFIG.DEFAULT_VALUE, // Start at 0.0
        dailyCompletions: 0,
        colorState: 'yellow',
        createdAt: new Date(),
        archived: false
      };

      console.log('🔥 Created habit object:', habit);

      // CRITICAL: Update local state FIRST and ensure it persists
      console.log('🎯 Dispatching CREATE_HABIT action for immediate UI update');
      dispatch({
        type: 'CREATE_HABIT',
        payload: { habit }
      });
      console.log('✅ CREATE_HABIT dispatched successfully');

      // Then save to Supabase in background - don't block UI on save failure
      try {
        console.log('💾 Saving habit to Supabase in background...');
        
        if (habit.type === 'habit' || habit.type === 'positive' || habit.type === 'negative') {
          await saveHabitToSupabase(habit);
        } else if (habit.type === 'daily') {
          await saveDailyToSupabase(habit);
        } else if (habit.type === 'todo') {
          await saveTodoToSupabase(habit);
        }
        
        console.log('✅ Habit saved to Supabase successfully');
      } catch (error) {
        console.error('❌ Error saving habit to Supabase (but kept in local state):', error);
        // Don't throw error here - we want to keep the habit in local state even if save fails
      }
    },

    updateHabit: async (habitId, habitData) => {
      console.log('🔄 updateHabit action called:', { 
        habitId, 
        habitData, 
        priority: habitData.priority,
        frequencyData: {
          frequency: (habitData as any).frequency,
          repeatType: (habitData as any).repeatType,
          repeatEvery: (habitData as any).repeatEvery,
          repeatOn: (habitData as any).repeatOn
        }
      });
      console.log('🔄 UPDATE_HABIT - About to dispatch action');
      const existingHabit = state.habits.find(h => h.id === habitId);
      if (!existingHabit) {
        console.log('❌ updateHabit - Habit not found:', habitId);
        return;
      }

      const updatedHabit = {
        ...habitData,
        // Preserve existing state that shouldn't be overwritten
        id: habitId,
        userId: existingHabit.userId,
        streak: existingHabit.streak,
        completedToday: existingHabit.completedToday,
        lastCompleted: existingHabit.lastCompleted,
        habitValue: existingHabit.habitValue,
        dailyCompletions: existingHabit.dailyCompletions,
        colorState: existingHabit.colorState
      };

      dispatch({
        type: 'UPDATE_HABIT',
        payload: { 
          habitId, 
          updatedHabit
        }
      });

      // Save to Supabase
      if (existingHabit.type === 'habit' || existingHabit.type === 'positive' || existingHabit.type === 'negative') {
        await saveHabitToSupabase(updatedHabit as HabitType);
      } else if (existingHabit.type === 'daily') {
        await saveDailyToSupabase(updatedHabit as HabitType);
      } else if (existingHabit.type === 'todo') {
        await saveTodoToSupabase(updatedHabit as HabitType);
      }
    },

    completeHabit: async (habitId) => {
      console.log('🎯 completeHabit action called for:', habitId);
      const habit = state.habits.find(h => h.id === habitId);
      if (!habit) {
        console.error('❌ Habit not found for completion:', habitId);
        return;
      }

      console.log('📊 Habit details before completion:', {
        id: habit.id,
        name: habit.name,
        type: habit.type,
        completedToday: habit.completedToday
      });

      // For dailies, prevent multiple completions
      if (habit.type === 'daily' && habit.completedToday) {
        console.log('⚠️ Daily already completed today, skipping');
        return;
      }

      console.log('✅ Dispatching COMPLETE_HABIT action');
      dispatch({
        type: 'COMPLETE_HABIT',
        payload: { habitId }
      });

      // Save completion to Supabase
      try {
        console.log('💾 Saving completion to Supabase...');
        if (habit.type === 'daily') {
          await markDailyCompleteInSupabase(habitId);
        } else if (habit.type === 'todo') {
          await markTodoCompleteInSupabase(habitId);
        } else if (habit.type === 'habit' || habit.type === 'positive' || habit.type === 'negative') {
          await logHabitCompletionToSupabase(habitId, habit.userId, 1, habit.points || 1);
        }
        console.log('✅ Completion saved to Supabase successfully');
      } catch (error) {
        console.error('❌ Error saving completion to Supabase:', error);
      }
    },

    uncompleteHabit: async (habitId) => {
      console.log('🔄 uncompleteHabit action called for:', habitId);
      const habit = state.habits.find(h => h.id === habitId);
      if (!habit) {
        console.error('❌ Habit not found for uncomplete:', habitId);
        return;
      }

      console.log('📊 Habit details before uncomplete:', {
        id: habit.id,
        name: habit.name,
        type: habit.type,
        completedToday: habit.completedToday,
        dailyCompletions: habit.dailyCompletions
      });
      
      // Only allow uncompleting if the habit was actually completed
      if (habit.type === 'habit' && habit.dailyCompletions === 0) {
        console.log('⚠️ Habit has no completions to undo');
        return;
      }
      if (habit.type === 'daily' && !habit.completedToday) {
        console.log('⚠️ Daily not completed today, nothing to undo');
        return;
      }
      if (habit.type === 'todo' && !habit.completedToday) {
        console.log('⚠️ Todo not completed today, nothing to undo');
        return;
      }

      console.log('↩️ Dispatching UNCOMPLETE_HABIT action');
      dispatch({
        type: 'UNCOMPLETE_HABIT',
        payload: { habitId }
      });

      // Save uncomplete to Supabase
      try {
        console.log('💾 Saving uncomplete to Supabase...');
        if (habit.type === 'habit' || habit.type === 'positive' || habit.type === 'negative') {
          await logHabitCompletionToSupabase(habitId, habit.userId, -1, -(habit.points || 1));
        } else if (habit.type === 'daily') {
          await markDailyUncompleteInSupabase(habitId);
        } else if (habit.type === 'todo') {
          // Mark todo as incomplete in Supabase
          await markTodoUncompleteInSupabase(habitId);
        }
        console.log('✅ Uncomplete saved to Supabase successfully');
      } catch (error) {
        console.error('❌ Error saving uncomplete to Supabase:', error);
      }
    },

    deleteHabit: async (habitId) => {
      console.log('🗑️ Delete habit action called:', habitId);
      console.log('🗑️ Current habits count:', state.habits.length);
      const habit = state.habits.find(h => h.id === habitId);
      
      dispatch({
        type: 'DELETE_HABIT',
        payload: { habitId }
      });

      // Delete from Supabase
      if (habit) {
        if (habit.type === 'habit' || habit.type === 'positive' || habit.type === 'negative') {
          await deleteHabitFromSupabase(habitId);
        } else if (habit.type === 'daily') {
          await deleteDailyFromSupabase(habitId);
        } else if (habit.type === 'todo') {
          await deleteTodoFromSupabase(habitId);
        }
      }
    },

    clickNegativeHabit: (habitId) => {
      const habit = state.habits.find(h => h.id === habitId);
      if (!habit || habit.type !== 'habit') return;

      const constitution = 10; // Base constitution without class system
      
      dispatch({
        type: 'CLICK_NEGATIVE_HABIT',
        payload: { habitId, constitution }
      });
    },

    takeDamage: (amount, reason) => {
      dispatch({
        type: 'TAKE_DAMAGE',
        payload: { amount, reason, timestamp: new Date() }
      });
    },



    applyDailyDamage: () => {
      const now = new Date();
      const lastCheck = new Date(state.lastDamageCheck);
      const daysSinceCheck = Math.floor((now.getTime() - lastCheck.getTime()) / (1000 * 60 * 60 * 24));

      if (daysSinceCheck > 0) {
        const incompleteDailies = state.habits.filter(h => 
          h.type === 'daily' && 
          !h.completedToday
        );

        if (incompleteDailies.length > 0) {
          const constitution = 10; // Base constitution without class system
          
          dispatch({
            type: 'APPLY_DAILY_DAMAGE',
            payload: { 
              incompleteDailies,
              constitution,
              newDamageCheck: now
            }
          });
        }
      }
    }
  };
};

